import React, {useState} from 'react';
import AppTable from '../../../../Presentation/components/ui/AppTable';
import {DashboardUser, UserStatus} from '../../models/user.model';
import {Badge} from '../../../../Presentation/components/ui/Badge';
import {AppText, AppTableText, AppSmallText} from '../../../../Presentation/components/ui/Typography';
import {RoleAssignmentModal} from '../modals/RoleAssignmentModal';
import {Edit, Trash2, UserCheck, UserX, Shield, KeyRound} from 'lucide-react';
import Button from '../../../../Presentation/components/ui/Button';
import {useAuthorization} from '../../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../../constants/permissions';
import DateTimeDisplay from '../../../../Presentation/components/ui/DateTimeDisplay';
import {
  getStatusBadgeVariant,
  getRoleDisplayName,
  getStatusDisplayName
} from './UsersTableHelpers';

interface UsersTableProps {
  data: DashboardUser[];
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onEdit?: (user: DashboardUser) => void;
  onDelete?: (userId: number) => void;
  onActivate?: (userId: number) => void;
  onDeactivate?: (userId: number) => void;
  onAssignRole?: (user: DashboardUser) => void;
  onChangePassword?: (user: DashboardUser) => void;
  showPageSizeSelector?: boolean;
  showPaginationInfo?: boolean;
  loading?: boolean;
}


export const UsersTable: React.FC<UsersTableProps> = ({
  data,
  page = 1,
  pageSize = 10,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  onAssignRole,
  onChangePassword,
  showPageSizeSelector = true,
  showPaginationInfo = true,
  loading = false
}) => {
  const {hasPermission} = useAuthorization();
  const [selectedUserForRole, setSelectedUserForRole] = useState<DashboardUser | null>(null);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  const columns = [
    {
      key: 'username',
      title: 'اسم المستخدم',
      render: (user: DashboardUser) => (
        <div className="flex items-center space-x-4 rtl:space-x-reverse py-2">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="font-semibold text-gray-700">
              {user.username.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <AppTableText className="font-semibold text-gray-900 block mb-1">
              {user.username}
            </AppTableText>
            {user.fullName && (
              <AppSmallText className="text-gray-600 block">
                {user.fullName}
              </AppSmallText>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'email',
      title: 'البريد الإلكتروني',
      render: (user: DashboardUser) => (
        <div className="py-2">
          <AppTableText className="text-gray-700 break-all">
            {user.email}
          </AppTableText>
        </div>
      )
    },
    {
      key: 'role',
      title: 'الدور',
      render: (user: DashboardUser) => (
        <div className="py-2">
          <Badge variant="outline" className="font-medium">
            {user.role ? getRoleDisplayName(user.role.name) : 'غير محدد'}
          </Badge>
        </div>
      )
    },
    {
      key: 'status',
      title: 'الحالة',
      render: (user: DashboardUser) => (
        <div className="py-2">
          <Badge variant={getStatusBadgeVariant(user.status)} className="font-medium">
            {getStatusDisplayName(user.status)}
          </Badge>
        </div>
      )
    },
    {
      key: 'createdAt',
      title: 'تاريخ الإنشاء',
      render: (user: DashboardUser) => (
        <DateTimeDisplay
          dateString={user.createdAt}
          dateClassName="text-sm text-gray-600"
          timeClassName="text-xs text-gray-500"
        />
      )
    }
  ];

  const handleOpenRoleModal = (user: DashboardUser) => {
    setSelectedUserForRole(user);
    setIsRoleModalOpen(true);
  };

  const handleCloseRoleModal = () => {
    setIsRoleModalOpen(false);
    setSelectedUserForRole(null);
  };

  const handleRoleAssignment = async (userId: number, roleId: number) => {
    if (onAssignRole && selectedUserForRole) {
      // Call the parent component's role assignment handler
      onAssignRole(selectedUserForRole);
    }
  };

  return (
    <div className="flex-1 min-h-[calc(100vh-200px)] flex flex-col">
      <div className="overflow-x-auto">
        <AppTable
          columns={columns}
          data={data}
          rowKey={(user) => user.id.toString()}
          emptyText="لم يتم العثور على مدراء"
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          showPageSizeSelector={showPageSizeSelector}
          showPaginationInfo={showPaginationInfo}
          loading={loading}
          loadingRows={pageSize || 10}
          loadingMessage="جاري تحميل المستخدمين..."
          className="text-sm"
          actions={(user) => {
            const actions = [];

            // Edit User - Requires edit permission
            if (hasPermission(PERMISSIONS.EDIT_USERS)) {
              actions.push({
                label: 'تعديل المستخدم',
                icon: <Edit className="text-yellow-600" size={20} />,
                onClick: () => onEdit?.(user)
              });
            }

            // Assign Role - Requires assign roles permission
            if (hasPermission(PERMISSIONS.ASSIGN_ROLES)) {
              actions.push({
                label: 'تعيين الدور',
                icon: <Shield className="text-purple-600" size={20} />,
                onClick: () => handleOpenRoleModal(user)
              });
            }

            // Activate/Deactivate - Requires edit permission
            if (hasPermission(PERMISSIONS.EDIT_USERS)) {
              actions.push({
                label: user.status === UserStatus.ACTIVE ? 'إلغاء تفعيل' : 'تفعيل',
                icon: user.status === UserStatus.ACTIVE
                  ? <UserX className="text-orange-600" size={20} />
                  : <UserCheck className="text-green-600" size={20} />,
                onClick: () => user.status === UserStatus.ACTIVE
                  ? onDeactivate?.(user.id)
                  : onActivate?.(user.id)
              });
            }

            // Reset Password - Requires edit permission (admin sets a new one)
            if (onChangePassword && hasPermission(PERMISSIONS.EDIT_USERS)) {
              actions.push({
                label: 'إعادة تعيين كلمة المرور',
                icon: <KeyRound className="text-brand-600" size={20} />,
                onClick: () => onChangePassword(user)
              });
            }

            // Delete User - Requires delete permission
            if (hasPermission(PERMISSIONS.DELETE_USERS)) {
              actions.push({
                label: 'حذف المستخدم',
                icon: <Trash2 className="text-red-600" size={20} />,
                onClick: () => onDelete?.(user.id),
                danger: true
              });
            }

            return actions;
          }}
        />
      </div>

      {/* Role Assignment Modal */}
      {selectedUserForRole && (
        <RoleAssignmentModal
          user={selectedUserForRole}
          isOpen={isRoleModalOpen}
          onClose={handleCloseRoleModal}
          onSubmit={handleRoleAssignment}
        />
      )}
    </div>
  );
};
